import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { usersAPI, adminAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Search, MoreVertical, Pencil, Trash2, UserPlus, Users, CreditCard, ArrowUpDown, ArrowUp, ArrowDown, Link2, Eye, Calendar } from 'lucide-react';
import { ROLE_COLORS, ROLE_LABELS } from '@/components/AppLayout';

// Utility function to format dates
const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatDateShort = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

export default function UserManagementPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [editUser, setEditUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [subscriptionUser, setSubscriptionUser] = useState(null);
  const [viewUserObcs, setViewUserObcs] = useState(null);
  const [userObcsData, setUserObcsData] = useState(null);
  const [loadingObcs, setLoadingObcs] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', role: '', integrator_id: '' });
  const [subscriptionForm, setSubscriptionForm] = useState({
    subscription_plan: '',
    subscription_status: '',
    subscription_end: '',
    extra_tickets: 0
  });
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  useEffect(() => { loadUsers(); loadPlans(); }, []);

  const loadUsers = async () => {
    try {
      const res = await usersAPI.getAll();
      setUsers(res.data);
    } catch (err) {
      toast.error('Errore nel caricamento utenti');
    } finally {
      setLoading(false);
    }
  };

  const loadPlans = async () => {
    try {
      const res = await adminAPI.getSubscriptionConfig();
      setPlans(res.data.plans || []);
    } catch (err) {
      console.error('Errore caricamento piani', err);
    }
  };

  const integrators = users.filter(u => u.role === 'integrator');
  
  const getIntegratorName = (integratorId) => {
    const integrator = integrators.find(i => i.user_id === integratorId);
    return integrator ? integrator.name : '';
  };

  const filteredUsers = users.filter(u =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.role?.toLowerCase().includes(search.toLowerCase())
  );

  // Sorting logic
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    if (!sortConfig.key) return 0;
    
    let aValue, bValue;
    switch (sortConfig.key) {
      case 'name':
        aValue = a.name?.toLowerCase() || '';
        bValue = b.name?.toLowerCase() || '';
        break;
      case 'email':
        aValue = a.email?.toLowerCase() || '';
        bValue = b.email?.toLowerCase() || '';
        break;
      case 'role':
        aValue = a.role?.toLowerCase() || '';
        bValue = b.role?.toLowerCase() || '';
        break;
      case 'integrator':
        aValue = getIntegratorName(a.integrator_id)?.toLowerCase() || '';
        bValue = getIntegratorName(b.integrator_id)?.toLowerCase() || '';
        break;
      case 'status':
        aValue = a.subscription_status?.toLowerCase() || '';
        bValue = b.subscription_status?.toLowerCase() || '';
        break;
      case 'created_at':
        aValue = new Date(a.created_at || 0).getTime();
        bValue = new Date(b.created_at || 0).getTime();
        break;
      case 'last_login':
        aValue = new Date(a.last_login || 0).getTime();
        bValue = new Date(b.last_login || 0).getTime();
        break;
      default:
        return 0;
    }
    
    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-50" />;
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1" /> 
      : <ArrowDown className="w-3 h-3 ml-1" />;
  };

  const handleViewObcs = async (u) => {
    setViewUserObcs(u);
    setLoadingObcs(true);
    try {
      const res = await adminAPI.getUserObcs(u.user_id);
      setUserObcsData(res.data);
    } catch (err) {
      toast.error('Errore nel caricamento OBC');
      setUserObcsData(null);
    } finally {
      setLoadingObcs(false);
    }
  };

  const handleEdit = (u) => {
    setEditUser(u);
    setEditForm({ name: u.name, role: u.role, integrator_id: u.integrator_id || '' });
  };

  const handleSaveEdit = async () => {
    try {
      await usersAPI.update(editUser.user_id, editForm);
      toast.success('Utente aggiornato');
      setEditUser(null);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore aggiornamento');
    }
  };

  const handleDelete = async () => {
    try {
      await usersAPI.delete(deleteUser.user_id);
      toast.success('Utente eliminato');
      setDeleteUser(null);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const handleEditSubscription = (u) => {
    setSubscriptionUser(u);
    setSubscriptionForm({
      subscription_plan: u.subscription_plan || 'free_trial',
      subscription_status: u.subscription_status || 'trial',
      subscription_end: u.subscription_end ? u.subscription_end.split('T')[0] : '',
      extra_tickets: u.extra_tickets || 0
    });
  };

  const handleSaveSubscription = async () => {
    try {
      await usersAPI.updateSubscription(subscriptionUser.user_id, subscriptionForm);
      toast.success('Abbonamento aggiornato');
      setSubscriptionUser(null);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore aggiornamento abbonamento');
    }
  };

  const availableRoles = currentUser?.role === 'root'
    ? ['root', 'administrator', 'integrator', 'user']
    : ['administrator', 'integrator', 'user'];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }} data-testid="users-page-title">
            Gestione Utenti
          </h1>
          <p className="text-muted-foreground mt-1">{users.length} utenti registrati</p>
        </div>
      </div>

      <Card className="border-border bg-card">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
              <Users className="w-5 h-5 inline mr-2" />
              Elenco Utenti
            </CardTitle>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cerca utenti..."
                data-testid="search-users"
                className="pl-10"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border">
                  <TableHead 
                    className="cursor-pointer hover:bg-secondary/50 select-none"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center">
                      Nome
                      <SortIcon columnKey="name" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-secondary/50 select-none"
                    onClick={() => handleSort('email')}
                  >
                    <div className="flex items-center">
                      Email
                      <SortIcon columnKey="email" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-secondary/50 select-none"
                    onClick={() => handleSort('role')}
                  >
                    <div className="flex items-center">
                      Ruolo
                      <SortIcon columnKey="role" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-secondary/50 select-none"
                    onClick={() => handleSort('integrator')}
                  >
                    <div className="flex items-center">
                      Integratore
                      <SortIcon columnKey="integrator" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-secondary/50 select-none"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center">
                      Stato
                      <SortIcon columnKey="status" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-secondary/50 select-none"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center">
                      Iscrizione
                      <SortIcon columnKey="created_at" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-secondary/50 select-none"
                    onClick={() => handleSort('last_login')}
                  >
                    <div className="flex items-center">
                      Ultimo Accesso
                      <SortIcon columnKey="last_login" />
                    </div>
                  </TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedUsers.map((u) => {
                  const assignedIntegrator = integrators.find(i => i.user_id === u.integrator_id);
                  return (
                    <TableRow key={u.user_id} className="border-border" data-testid={`user-row-${u.user_id}`}>
                      <TableCell className="font-medium">{u.name}</TableCell>
                      <TableCell className="text-muted-foreground">{u.email}</TableCell>
                      <TableCell>
                        <Badge className={ROLE_COLORS[u.role]}>
                          {ROLE_LABELS[u.role]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {assignedIntegrator ? assignedIntegrator.name : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={u.subscription_status === 'active' ? 'text-green-500 border-green-500/30' : 'text-yellow-500 border-yellow-500/30'}>
                          {u.subscription_status === 'trial' ? 'Prova' : u.subscription_status === 'active' ? 'Attivo' : u.subscription_status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatDateShort(u.created_at)}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatDateShort(u.last_login)}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`user-actions-${u.user_id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-popover border-border">
                            <DropdownMenuItem
                              data-testid={`view-obcs-${u.user_id}`}
                              onClick={() => handleViewObcs(u)}
                              className="cursor-pointer"
                            >
                              <Eye className="w-4 h-4 mr-2" /> Visualizza OBC
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              data-testid={`edit-user-${u.user_id}`}
                              onClick={() => handleEdit(u)}
                              className="cursor-pointer"
                            >
                              <Pencil className="w-4 h-4 mr-2" /> Modifica
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              data-testid={`edit-subscription-${u.user_id}`}
                              onClick={() => handleEditSubscription(u)}
                              className="cursor-pointer"
                            >
                              <CreditCard className="w-4 h-4 mr-2" /> Modifica Abbonamento
                            </DropdownMenuItem>
                            {u.user_id !== currentUser?.user_id && (
                              <DropdownMenuItem
                                data-testid={`delete-user-${u.user_id}`}
                                onClick={() => setDeleteUser(u)}
                                className="cursor-pointer text-destructive focus:text-destructive"
                              >
                                <Trash2 className="w-4 h-4 mr-2" /> Elimina
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {sortedUsers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      Nessun utente trovato
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Modifica Utente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Info dates - read only */}
            <div className="grid grid-cols-2 gap-4 p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Data Iscrizione</p>
                  <p className="text-sm font-medium">{formatDate(editUser?.created_at)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Ultimo Accesso</p>
                  <p className="text-sm font-medium">{formatDate(editUser?.last_login)}</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input
                data-testid="edit-user-name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Ruolo</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                <SelectTrigger data-testid="edit-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  {availableRoles.map(r => (
                    <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Integratore Assegnato</Label>
              <Select
                value={editForm.integrator_id || 'none'}
                onValueChange={(v) => setEditForm({ ...editForm, integrator_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger data-testid="edit-user-integrator">
                  <SelectValue placeholder="Nessuno" />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="none">Nessuno</SelectItem>
                  {integrators.map(i => (
                    <SelectItem key={i.user_id} value={i.user_id}>{i.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)}>Annulla</Button>
            <Button data-testid="save-user-edit" onClick={handleSaveEdit} className="bg-primary text-primary-foreground">
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteUser} onOpenChange={() => setDeleteUser(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: 'Manrope' }}>Conferma Eliminazione</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare l'utente <strong>{deleteUser?.name}</strong>? Questa azione non puo' essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-user"
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Subscription Edit Dialog */}
      <Dialog open={!!subscriptionUser} onOpenChange={() => setSubscriptionUser(null)}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              <CreditCard className="w-5 h-5 inline mr-2" />
              Modifica Abbonamento
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-secondary/50 rounded-lg">
              <p className="text-sm text-muted-foreground">Utente: <strong>{subscriptionUser?.name}</strong></p>
              <p className="text-xs text-muted-foreground">{subscriptionUser?.email}</p>
            </div>
            
            <div className="space-y-2">
              <Label>Piano</Label>
              <Select 
                value={subscriptionForm.subscription_plan} 
                onValueChange={(v) => setSubscriptionForm({ ...subscriptionForm, subscription_plan: v })}
              >
                <SelectTrigger data-testid="edit-subscription-plan">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="free_trial">Prova Gratuita</SelectItem>
                  {plans.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Stato</Label>
              <Select 
                value={subscriptionForm.subscription_status} 
                onValueChange={(v) => setSubscriptionForm({ ...subscriptionForm, subscription_status: v })}
              >
                <SelectTrigger data-testid="edit-subscription-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="trial">Prova</SelectItem>
                  <SelectItem value="active">Attivo</SelectItem>
                  <SelectItem value="expired">Scaduto</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Data Scadenza</Label>
              <Input
                type="date"
                data-testid="edit-subscription-end"
                value={subscriptionForm.subscription_end}
                onChange={(e) => setSubscriptionForm({ ...subscriptionForm, subscription_end: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Lascia vuoto per impostare automaticamente 1 anno dalla data odierna
              </p>
            </div>

            <div className="space-y-2">
              <Label>Ticket Extra</Label>
              <Input
                type="number"
                min={0}
                data-testid="edit-subscription-tickets"
                value={subscriptionForm.extra_tickets}
                onChange={(e) => setSubscriptionForm({ ...subscriptionForm, extra_tickets: parseInt(e.target.value) || 0 })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubscriptionUser(null)}>Annulla</Button>
            <Button data-testid="save-subscription-edit" onClick={handleSaveSubscription} className="bg-primary text-primary-foreground">
              Salva Abbonamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View User OBCs Dialog */}
      <Dialog open={!!viewUserObcs} onOpenChange={() => { setViewUserObcs(null); setUserObcsData(null); }}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              <Link2 className="w-5 h-5 inline mr-2" />
              OBC di {viewUserObcs?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            {loadingObcs ? (
              <div className="text-center py-8 text-muted-foreground">Caricamento...</div>
            ) : userObcsData ? (
              <div className="space-y-4">
                <div className="p-3 bg-secondary/50 rounded-lg">
                  <p className="text-sm"><strong>Email:</strong> {userObcsData.user_email}</p>
                  <p className="text-sm"><strong>Ruolo:</strong> {ROLE_LABELS[userObcsData.user_role] || userObcsData.user_role}</p>
                  <p className="text-sm"><strong>OBC Totali:</strong> {userObcsData.obc_count}</p>
                </div>
                {userObcsData.obcs.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border">
                        <TableHead>Nome OBC</TableHead>
                        <TableHead>Token</TableHead>
                        <TableHead>Motivo Accesso</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {userObcsData.obcs.map((obc) => (
                        <TableRow key={obc.link_id} className="border-border">
                          <TableCell className="font-medium">{obc.name}</TableCell>
                          <TableCell>
                            <code className="px-2 py-1 rounded bg-secondary text-xs font-mono">
                              {obc.redeemed_token || '-'}
                            </code>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {obc.reason === 'admin_access' ? 'Admin' : 
                               obc.reason === 'managed_user' ? 'Utente Gestito' :
                               obc.reason === 'own_obc' ? 'Proprio' : 'Assegnato'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    Nessun OBC trovato per questo utente
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                Errore nel caricamento dei dati
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setViewUserObcs(null); setUserObcsData(null); }}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
